// sessionStore — the `session.status` state machine (contract 07 §7/§8, ledger A5).
//
// 07 is the SINGLE writer of every `status` transition; the shell (05) only reads it via
// `useAccountSession()`. The store is deliberately tiny (`status`, `bootError`, `hasBooted`)
// and kept OUT of every persist path: a hard refresh must re-derive `idle` so the always-mounted
// boot gate re-runs a fresh bootstrap ("refresh = fresh bootstrap", contract §7/§14). It carries
// NO `persist` option for exactly that reason — see the persistence assertion in its spec.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { BootstrapSection, DegradedSection } from '../models/bootstrap.types'
import type { SessionStatus } from '../models/session.types'

import type { ApiError } from '@/shared/http'

export const useSessionStore = defineStore('accountSession', () => {
  const status = ref<SessionStatus>('idle')
  const bootError = ref<ApiError | null>(null)
  // Latches once the first boot reaches `ready`; the flow reads it (with `status`) to
  // short-circuit a concurrent double-boot — one boot per token (contract §14).
  const hasBooted = ref(false)
  // Sections the current boot could NOT resolve (`failed`/`cold`, BE-ACC-06), recorded by the
  // boot flow per response. A boot that reaches `ready` with entries here is a DEGRADED READY —
  // never an `error`, never a logout; the (future) shell reads this to offer a section retry.
  const degradedSections = ref<DegradedSection[]>([])

  const isReady = computed(() => status.value === 'ready')
  const isBooting = computed(() => status.value === 'booting')
  /** `ready` with unresolved sections — degraded, not blocked (contract §14). */
  const isDegraded = computed(() => status.value === 'ready' && degradedSections.value.length > 0)

  /** idle/ready/error → booting. Clears any prior error AND the degraded record so a retry
   *  starts clean (a fresh boot re-derives which sections are unresolved). */
  function beginBoot(): void {
    status.value = 'booting'
    bootError.value = null
    degradedSections.value = []
  }

  /**
   * Union-merge the degraded record of one bootstrap response (keyed by section, last write
   * wins). A boot is several fetches over DISJOINT section sets (account-level, workspace-level,
   * deferred compositions), so entries accumulate; `beginBoot` is the only reset on the boot path.
   */
  function recordDegradedSections(sections: readonly DegradedSection[]): void {
    if (!sections.length) return
    const merged = new Map(degradedSections.value.map((d) => [d.section, d]))
    for (const entry of sections) merged.set(entry.section, entry)
    degradedSections.value = [...merged.values()]
  }

  /**
   * Settle one PARTIAL retry (ORP-20409): drop every section it re-asked for, then re-record the
   * ones the server still could not resolve. `recordDegradedSections` alone cannot do this — it is
   * a union merge, so a section that came back CLEAN carries no entry to overwrite the stale one
   * and would stay listed as degraded forever.
   *
   * `attempted` is the request, not the response, precisely so a silent section is treated as
   * resolved. Nothing here touches `status`: a section retry is not a re-boot (contract §7/§14).
   */
  function resolveDegradedSections(
    attempted: readonly BootstrapSection[],
    stillDegraded: readonly DegradedSection[],
  ): void {
    const dropped: ReadonlySet<string> = new Set<string>(attempted)
    const kept = degradedSections.value.filter((entry) => !dropped.has(entry.section))
    const merged = new Map(kept.map((entry) => [entry.section, entry]))
    for (const entry of stillDegraded) merged.set(entry.section, entry)
    degradedSections.value = [...merged.values()]
  }

  /** booting → ready. Clears the error and latches `hasBooted`. */
  function markReady(): void {
    status.value = 'ready'
    bootError.value = null
    hasBooted.value = true
  }

  /** booting → error, recording the boot failure the retry surface renders (contract §14). */
  function markError(error: ApiError): void {
    status.value = 'error'
    bootError.value = error
  }

  /** Logout reset (auth 06 → resetAllStores): back to a pristine `idle`, no boot memory. */
  function reset(): void {
    status.value = 'idle'
    bootError.value = null
    hasBooted.value = false
    degradedSections.value = []
  }

  return {
    status,
    bootError,
    hasBooted,
    degradedSections,
    isReady,
    isBooting,
    isDegraded,
    beginBoot,
    markReady,
    markError,
    recordDegradedSections,
    resolveDegradedSections,
    reset,
  }
})

export type SessionStore = ReturnType<typeof useSessionStore>
